import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import React, { useCallback, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';

import { ActivityIndicator, Card, CardContent, Text } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import {
	useBillAttachments,
	useCreateAttachment,
	useDeleteAttachment,
	useInvoiceAttachments,
	usePaymentAttachments,
} from '@/hooks/use-data';
import { type Attachment } from '@/types/models';
import { Alert } from '@/utils/alert';
import { pickedDocumentFormData } from '@/utils/native-form-file';
import { openFetchedFile } from '@/utils/open-fetched-pdf';
import { safeTabBlobUrl, saveResponseAs } from '@/utils/safe-blob-types';

type ParentType = 'bill' | 'invoice' | 'payment';

interface Props {
	parentType: ParentType;
	parentId: string;
	editable?: boolean;
}

const fmtSize = (bytes: number): string => {
	if (!bytes) return '';
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const iconForMime = (mime?: string): keyof typeof Ionicons.glyphMap => {
	if (!mime) return 'document-outline';
	if (mime.startsWith('image/')) return 'image-outline';
	if (mime === 'application/pdf') return 'document-text-outline';
	return 'document-outline';
};

export const AccountingAttachments: React.FC<Props> = ({ parentType, parentId, editable = true }) => {
	const billQuery = useBillAttachments(parentType === 'bill' ? parentId : undefined);
	const invoiceQuery = useInvoiceAttachments(parentType === 'invoice' ? parentId : undefined);
	const paymentQuery = usePaymentAttachments(parentType === 'payment' ? parentId : undefined);
	const query = parentType === 'bill' ? billQuery : parentType === 'invoice' ? invoiceQuery : paymentQuery;

	const { uploadFileToServer, downloadFile } = useApi();
	const createMutation = useCreateAttachment();
	const deleteMutation = useDeleteAttachment();

	const [busy, setBusy] = useState(false);

	const scopeKey = parentType === 'bill' ? 'billId' : parentType === 'invoice' ? 'invoiceId' : 'paymentId';

	const onPick = useCallback(async () => {
		const res = await DocumentPicker.getDocumentAsync({
			type: ['image/*', 'application/pdf'],
			multiple: false,
			copyToCacheDirectory: true,
		});
		if (res.canceled || !res.assets?.[0]) return;
		const asset = res.assets[0];
		setBusy(true);
		try {
			const formData = await pickedDocumentFormData(asset, { attachmentType: 'misc', [scopeKey]: parentId });
			const uploadResult = await uploadFileToServer(formData);
			if (!uploadResult?.success) throw new Error('Upload failed');
			await createMutation.mutateAsync({
				[scopeKey]: parentId,
				fileName: uploadResult.fileName,
				fileType: uploadResult.fileType,
				mimeType: uploadResult.mimeType,
				fileSize: uploadResult.fileSize,
				storageKey: uploadResult.storageKey,
				attachmentType: 'misc',
			} as any);
			await query.refetch();
		} catch (e) {
			Alert.alert('Upload failed', e instanceof Error ? e.message : 'Unknown error');
		} finally {
			setBusy(false);
		}
	}, [scopeKey, parentId, uploadFileToServer, createMutation, query]);

	const onOpen = useCallback(async (att: Attachment) => {
		if (!att.storageKey) return;
		const mime = att.mimeType || 'application/octet-stream';
		try {
			if (Platform.OS !== 'web') {
				// iOS has no `window.open` and no blob URLs: cache file + share sheet instead.
				await openFetchedFile(() => downloadFile(`/files/${att.storageKey}`), att.fileName || 'attachment', mime);
				return;
			}
			const response = await downloadFile(`/files/${att.storageKey}`);
			// `downloadFile` hands back a non-2xx Response rather than throwing; re-typing that
			// error body would open a broken "PDF" or save a corrupt file.
			if (!response.ok) throw new Error(`Could not open ${att.fileName || 'the attachment'} (${response.status})`);
			// Rendered only as a safe, re-typed blob; anything else is saved (safe-blob-types.ts).
			const url = await safeTabBlobUrl(response, att.fileName ?? '');
			if (!url) {
				await saveResponseAs(response, att.fileName || 'attachment');
				return;
			}
			window.open(url, '_blank');
			setTimeout(() => URL.revokeObjectURL(url), 60_000);
		} catch (e) {
			Alert.alert('Open failed', e instanceof Error ? e.message : 'Unknown error');
		}
	}, [downloadFile]);

	const onDelete = useCallback((att: Attachment) => {
		Alert.alert('Delete attachment?', att.fileName, [
			{ text: 'Cancel', style: 'cancel' },
			{
				text: 'Delete',
				style: 'destructive',
				onPress: async () => {
					try {
						await deleteMutation.mutateAsync(att.id);
						await query.refetch();
					} catch (e) {
						Alert.alert('Delete failed', e instanceof Error ? e.message : 'Unknown error');
					}
				},
			},
		]);
	}, [deleteMutation, query]);

	const items = query.data ?? [];

	return (
		<Card>
			<CardContent>
				<View style={styles.header}>
					<Text style={styles.section}>Attachments</Text>
					{editable && (
						<Pressable onPress={onPick} disabled={busy} style={styles.uploadBtn}>
							<Ionicons name="cloud-upload-outline" size={16} color="#1976d2" />
							<Text style={styles.uploadLabel}>{busy ? 'Uploading…' : 'Add file'}</Text>
						</Pressable>
					)}
				</View>
				{query.isLoading ? (
					<ActivityIndicator />
				) : items.length === 0 ? (
					<Text style={styles.empty}>No attachments yet.</Text>
				) : (
					items.map((att: Attachment) => (
						<View key={att.id} style={styles.row}>
							<Pressable onPress={() => onOpen(att)} style={styles.rowMain} hitSlop={4}>
								<Ionicons name={iconForMime(att.mimeType)} size={20} color="#546e7a" />
								<View style={{ flex: 1 }}>
									<Text style={styles.fileName} numberOfLines={1}>{att.fileName}</Text>
									<Text style={styles.fileSub}>{att.mimeType ?? '—'} · {fmtSize(att.fileSize)}</Text>
								</View>
							</Pressable>
							{editable && (
								<Pressable onPress={() => onDelete(att)} hitSlop={6}>
									<Ionicons name="trash-outline" size={18} color="#c62828" />
								</Pressable>
							)}
						</View>
					))
				)}
			</CardContent>
		</Card>
	);
};

const styles = StyleSheet.create({
	header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
	section: { fontWeight: '600', fontSize: 15 },
	uploadBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 8 },
	uploadLabel: { color: '#1976d2', fontSize: 13, fontWeight: '500' },
	empty: { color: '#888', fontStyle: 'italic', paddingVertical: 6 },
	row: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: '#e0e0e0' },
	rowMain: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
	fileName: { fontSize: 14 },
	fileSub: { color: '#90a4ae', fontSize: 12 },
});

export default AccountingAttachments;
